import { useEffect, useRef } from "react";
import {
    scrollElementIntoViewOnKeyboardVisible,
    adjustContentIntoView,
    restoreContentIntoView,
    getContentNode,
    updateHtmlElementHeightAccToKeyboardHeight,
} from "@checkout-core/utils/Dom";

const BOTTOM_OFFSET = 24;

const useKeyboardState = (htmlElRef?: React.RefObject<HTMLElement>): void => {
    const resizeObserver = useRef<ResizeObserver | null>(null);
    const keyboardHeightRef = useRef<number>(0);
    const isUpdatingHeightRef = useRef<boolean>(false);
    const observedElementRef = useRef<HTMLElement | null>(null);
    const lastKnownHeightRef = useRef<number>(0);

    useEffect(() => {
        let timer: number | null = null;
        let container: HTMLElement | null = null;
        let canAdjustHeight = true;
        const oldFn = window.keyboardStateChanged;

        const clearTimer = (): void => {
            timer && window.cancelAnimationFrame(timer);
        };

        const reset = (container: HTMLElement | null): void => {
            clearTimer();
            restoreContentIntoView(container);
            canAdjustHeight = true;
        };

        if (htmlElRef?.current) {
            // Setup ResizeObserver to update min-height when content changes
            if (typeof ResizeObserver !== "undefined") {
                resizeObserver.current = new ResizeObserver((entries) => {
                    // Only update height if there's an actual content change and keyboard is visible
                    const entry = entries[0];
                    const currentHeight = entry.contentRect.height;
                    const hasContentChange = currentHeight !== lastKnownHeightRef.current;

                    if (
                        htmlElRef?.current &&
                        keyboardHeightRef.current > 0 &&
                        !isUpdatingHeightRef.current &&
                        hasContentChange
                    ) {
                        isUpdatingHeightRef.current = true;

                        // Calculate the height difference
                        const heightDifference = currentHeight - lastKnownHeightRef.current;

                        // Get the current min-height
                        const currentMinHeight = Number(
                            htmlElRef.current.style.minHeight?.replace("px", "") || htmlElRef.current.clientHeight,
                        );

                        // Update min-height by adding the height difference
                        htmlElRef.current.style.setProperty("min-height", `${currentMinHeight + heightDifference}px`);

                        // Update last known height
                        lastKnownHeightRef.current = currentHeight;

                        isUpdatingHeightRef.current = false;
                    }
                });
            }
        }

        if (htmlElRef?.current && typeof oldFn === "function") {
            oldFn(false, 0);
        }

        window.keyboardStateChanged = (visible: boolean, keyboardHeight: number): void => {
            keyboardHeightRef.current = visible ? keyboardHeight : 0;

            if (htmlElRef?.current) {
                isUpdatingHeightRef.current = true;
                updateHtmlElementHeightAccToKeyboardHeight(htmlElRef.current, visible, keyboardHeight);

                // Start observing the focused element when keyboard opens
                if (visible) {
                    const focusedElement = document.activeElement;

                    if (focusedElement instanceof HTMLElement && focusedElement !== observedElementRef.current) {
                        // Stop observing previous element if any
                        resizeObserver.current?.disconnect();
                        // Start observing new element
                        resizeObserver.current?.observe(focusedElement);
                        observedElementRef.current = focusedElement;
                        // Store initial height of the textarea
                        lastKnownHeightRef.current = focusedElement.clientHeight;
                    }
                } else {
                    // Clear observed element when keyboard closes
                    observedElementRef.current = null;
                    lastKnownHeightRef.current = 0;
                }

                isUpdatingHeightRef.current = false;
                return;
            }

            /**
             * if keyboard is visible,
             *  => scroll the focused elemnt into view if required
             *  => change content height w.r.t to keyboard
             * else
             *  => restore content height to full view avaialble
             */
            if (visible) {
                const focusedElement = document.activeElement;
                if (!focusedElement) {
                    return;
                }

                const rect = focusedElement.getBoundingClientRect();
                // take existing container instance; when switching from one focused to another focused
                const newContainer = getContentNode(focusedElement, keyboardHeight);
                if (newContainer !== container) {
                    reset(container);
                }

                container = newContainer;

                clearTimer();
                timer = window.requestAnimationFrame(() => {
                    canAdjustHeight && adjustContentIntoView(container, keyboardHeight);
                    canAdjustHeight = false;
                    scrollElementIntoViewOnKeyboardVisible({
                        container,
                        rect,
                        bottomOffset: BOTTOM_OFFSET,
                    });
                });
            } else {
                reset(container);
            }
        };

        return (): void => {
            clearTimer();
            window.keyboardStateChanged = oldFn;
            resizeObserver.current?.disconnect();
            observedElementRef.current = null;
            lastKnownHeightRef.current = 0;
        };
    }, [htmlElRef]);
};

export { useKeyboardState };
